import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Link,
  Avatar,
  Button,
  Breadcrumbs,
  IconButton,
  TextField,
  Paper,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  List,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AccountBalance as DepartmentIcon,
  Language as LanguageIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { axiosService, fetcher, useUser } from 'shared/helpers/axios';
import BackButton from 'components/common/buttons/BackButton';
import { useTheme } from '@mui/material/styles';
import InfrastructureEditor from './InfrastructureEditor';

const DepartmentPage = () => {
  const { orgId, deptId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const { user: currentUser} = useUser();

  // Fetch department data
  const { data: dept, error, isLoading, mutate } = useSWR(
    `/consortium/organizations/${orgId}/departments/${deptId}/`,
    fetcher
  );

  // Fetch organization data for context
  const { data: org } = useSWR(
    `/consortium/organizations/${orgId}/`,
    fetcher
  );

  // Fetch countries
  const { data: countries = [] } = useSWR('/consortium/countries/', fetcher);


  // Fetch department members
  const { data: membersData } = useSWR(
    `/consortium/organization-memberships/?department=${deptId}`,
    fetcher
  );

  const members = membersData?.results || [];
  const isMember = members.some(m => m.status === 'active');

  // State
  const [editing, setEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: ''
  });


  // Check if user can edit this department
  const canEditDep = React.useMemo(() => {
    if (!deptId || !currentUser) return false;
    if (currentUser?.is_superuser || currentUser?.is_staff) return true;
    const membership = currentUser?.org_memberships?.find(
      m => m?.department_id === deptId || m?.department_id === parseInt(deptId, 10)
    );
    return membership?.can_edit_org ?? false;
  }, [deptId, currentUser]);


  // Infrastructure states
  const [infrastructureItems, setInfrastructureItems] = useState([]);
  const [newInfraName, setNewInfraName] = useState('');
  const [newInfraDesc, setNewInfraDesc] = useState('');
  const [newInfraCategory, setNewInfraCategory] = useState('');

  const userCountryCode = currentUser?.country?.code || 'USA';

  // Address state
  const [address, setAddress] = useState(null);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addressForm, setAddressForm] = useState({
    line1: '',
    line2: '',
    city: '',
    state_region: '',
    postal_code: '',
    country_code: userCountryCode,
    type: 'business'
  });

  // Initialize form when dept loads
  React.useEffect(() => {
    if (dept) {
      setFormData({
        name: dept.name || '',
        description: dept.description || '',
        website: dept.website || ''
      });
      setInfrastructureItems(dept.infrastructure || []);
      setAddress(dept.address || null);
    }
  }, [dept]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setUpdating(true);
      await axiosService.patch(`/consortium/organizations/${orgId}/departments/${deptId}/`, formData);
      setSnackbar({ open: true, message: 'Department updated successfully', severity: 'success' });
      mutate();
      setEditing(false);
    } catch (e) {
      console.error('Failed to save department', e);
      setSnackbar({ open: true, message: 'Failed to save department', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteDepartment = async () => {
    if (!window.confirm('Are you sure you want to delete this department? This action cannot be undone.')) {
      return;
    }

    try {
      setUpdating(true);
      await axiosService.delete(`/consortium/organizations/${orgId}/departments/${deptId}/`);
      setSnackbar({ open: true, message: 'Department deleted successfully', severity: 'success' });
      // Navigate back to organization page after deletion
      setTimeout(() => {
        navigate(`/organization/${orgId}/`);
      }, 1000);
    } catch (e) {
      console.error('Failed to delete department', e);
      setSnackbar({ open: true, message: e.response?.data?.detail || 'Failed to delete department', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenAddressDialog = () => {
    if (address) {
      setAddressForm({
        line1: address.line1 || '',
        line2: address.line2 || '',
        city: address.city || '',
        state_region: address.state_region || '',
        postal_code: address.postal_code || '',
        country_code: address.country?.code || address.country_code || userCountryCode,
        type: address.type || 'business'
      });
    } else {
      setAddressForm({
        line1: '',
        line2: '',
        city: '',
        state_region: '',
        postal_code: '',
        country_code: userCountryCode,
        type: 'business'
      });
    }
    setAddressDialogOpen(true);
  };

  const handleCloseAddressDialog = () => {
    setAddressDialogOpen(false);
  };

  const handleSaveAddress = async () => {
    try {
      setUpdating(true);
      await axiosService.post(`/consortium/organizations/${orgId}/departments/${deptId}/update-address/`, addressForm);
      setSnackbar({ open: true, message: 'Address saved successfully', severity: 'success' });
      mutate();
      handleCloseAddressDialog();
    } catch (e) {
      console.error('Failed to save address:', e);
      setSnackbar({ open: true, message: 'Failed to save address', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteAddress = async () => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    
    try {
      setUpdating(true);
      await axiosService.delete(`/consortium/organizations/${orgId}/departments/${deptId}/remove-address/`);
      setSnackbar({ open: true, message: 'Address deleted successfully', severity: 'success' });
      setAddress(null);
      mutate();
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to delete address', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">Failed to load department</Alert>
      </Container>
    );
  }

  return (
 

    <Container maxWidth="lg" sx={{ py: 4, px:4 }}>
  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
    <BackButton position="inline" />
    <Box sx={{ flex: 1 }}>
      {org && (
        <Breadcrumbs aria-label="breadcrumb">
          {/* First breadcrumb: Organization */}
          <Link
            underline="hover"
            color="text.secondary"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate(`/organization/${orgId}`);
            }}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <BusinessIcon fontSize="small" color="text.secondary" />
            {org.name}
          </Link>
          
          {/* Second breadcrumb: Department */}
          {dept && (
            <Typography
              color="text.primary"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {dept.name}
            </Typography>
          )}
        </Breadcrumbs>
      )}
    </Box>
  </Box>



      {/* Header */}


      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>


        <Paper sx={{ px: 3, py:2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Avatar sx={{ width: 80, height: 80, bgcolor: 'secondary.main' }}>
              <DepartmentIcon sx={{ fontSize: 40 }} />
            </Avatar>

            
            <Box>
              {editing ? (
                <TextField
                  label="Department Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  fullWidth
                  required
                  sx={{ mb: 1, minWidth: 300 }}
                />
              ) : (
                <Typography variant="h4" fontWeight={600}>
                  {dept?.name}
                </Typography>
              )}
              
              <Typography variant="body2" color="text.secondary">
                {org?.name}
              </Typography>
            </Box>

            

          </Box>



         { canEditDep && (

          <Box sx={{ display: 'flex', gap: 1 }}>
            {editing ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => {
                    setEditing(false);
                    setFormData({
                      name: dept.name,
                      description: dept.description,
                      website: dept.website
                    });
                  }}
                  disabled={updating}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={updating || !formData.name}
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => setEditing(true)}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteDepartment}
                  disabled={updating}
                >
                  Delete Department
                </Button>
              </>
            )}
          </Box>

          )}

        </Box>

        {/* Website */}
        {editing ? (
          <TextField
            label="Website"
            value={formData.website}
            onChange={(e) => handleInputChange('website', e.target.value)}
            fullWidth
            placeholder="https://example.com"
            InputProps={{
              startAdornment: <LanguageIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
        ) : dept?.website && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <LanguageIcon color="action" />
            <Typography
              component="a"
              href={dept.website}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {dept.website}
            </Typography>
          </Box>
        )}



{editing ? (
                <TextField
                  multiline
                  rows={6}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  fullWidth
                  placeholder="Enter department description..."
                />
              ) : (
                <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {dept?.description || 'No description provided.'}
                </Typography>
              )}

      </Paper>

          {/* Description */}
          {/* <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Description</Typography>

            </CardContent>
          </Card> */}

          {/* Infrastructure */}
          {/* <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Infrastructure</Typography>
              
              <Stack spacing={2} sx={{ mb: 2 }}>
                {infrastructureItems.map((item, index) => (
                  <Paper key={index} sx={{ p: 2, position: 'relative' }}>
                    <IconButton
                      size="small"
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                      onClick={() => handleRemoveInfrastructure(index)}
                      disabled={updating}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="subtitle2" fontWeight={600}>{item.name}</Typography>
                    {item.category && (
                      <Typography variant="caption" color="text.secondary">
                        Category: {item.category}
                      </Typography>
                    )}
                    {item.description && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {item.description}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Added by: {item.added_by}
                    </Typography>
                  </Paper>
                ))}
              </Stack>

              {infrastructureItems.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No infrastructure items added yet.
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              <Stack spacing={2}>
                <TextField
                  label="Infrastructure Name"
                  value={newInfraName}
                  onChange={(e) => setNewInfraName(e.target.value)}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Description"
                  value={newInfraDesc}
                  onChange={(e) => setNewInfraDesc(e.target.value)}
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                />
                <TextField
                  label="Category"
                  value={newInfraCategory}
                  onChange={(e) => setNewInfraCategory(e.target.value)}
                  size="small"
                  fullWidth
                />
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddInfrastructure}
                  disabled={!newInfraName.trim() || updating}
                >
                  Add Infrastructure
                </Button>
              </Stack>
            </CardContent>
          </Card> */}






 


          
          {/* Members */}

          {isMember && (
            <Card sx={{ mb: 3, mt:3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <PeopleIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Members ({members.length})
              </Typography>
              
              {members.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No members assigned to this department yet.
                </Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Start Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {members.map((membership) => (
                        <TableRow key={membership.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 32, height: 32,
                                 backgroundColor: theme.palette.primary.avatar,
                                 color: 'white',
                               }}>
                                <PersonIcon fontSize="small" />
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {membership.user_name || 'Unknown'}
                                </Typography>
                                {membership.user_email && (
                                  <Typography variant="caption" color="text.secondary">
                                    {membership.user_email}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={membership.role} size="small" />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={membership.status} 
                              size="small" 
                              color={membership.status === 'active' ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {membership.start_date ? new Date(membership.start_date).toLocaleDateString() : '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
          )}



<InfrastructureEditor
  infrastructureItems={infrastructureItems}
  setInfrastructureItems={setInfrastructureItems}
  canEdit={canEditDep}
  addUrl={`/consortium/organizations/${orgId}/departments/${deptId}/add-infrastructure/`}
  removeUrl={`/consortium/organizations/${orgId}/departments/${deptId}/remove-infrastructure/`}
  mutate={mutate}
  setSnackbar={setSnackbar}
/>




        </Grid>




        {/* Right Column - Stats & Info */}
        <Grid item xs={12} md={4}>



        {/* Address */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">
                  <LocationIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Address
                </Typography>
                {address && canEditDep ? (
                  <Box>
                    
                          <IconButton
                              size="small"
                              onClick={() => handleOpenAddressDialog()}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteAddress()}
                              disabled={updating}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>



                  </Box>
                ) : (
                    canEditDep &&
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleOpenAddressDialog}
                  >
                    Add Address
                  </Button>
                )}
              </Box>

              {address ? (
                <Paper sx={{ px: 2, py: 1, backgroundColor: 'background.default', border: 'none', boxShadow: 'none' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="start">
                  <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {address.line1}
                  </Typography>
                  {address.line2 && (
                    <Typography variant="body2">{address.line2}</Typography>
                  )}
                  <Typography variant="body2">
                    {[address.city, address.state_region, address.postal_code].filter(Boolean).join(', ')}
                  </Typography>
                  <Typography variant="body2">
                    {address.country?.name || address.country_code || 'N/A'}
                  </Typography>
                  </Box>
                  {/* <Box>
                  <Chip label={address.type} size="small" sx={{ mt: 1 }} />
                  </Box> */}
                  </Stack>
                </Paper>
                
              ) : (
                <Alert severity="info">No address added yet.</Alert>
              )}
            </CardContent>
          </Card>


          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Statistics</Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Total Members</Typography>
                  <Typography variant="h5">{members.length}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Active Members</Typography>
                  <Typography variant="h5">
                    {members.filter(m => m.status === 'active').length}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Infrastructure Items</Typography>
                  <Typography variant="h5">{infrastructureItems.length}</Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>Quick Info</Typography>
              <Stack spacing={1}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Organization</Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => navigate(`/organization/${orgId}`)}
                  >
                    {org?.name}
                  </Typography>
                </Box>
                {dept?.created && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Created</Typography>
                    <Typography variant="body2">
                      {new Date(dept.created).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
                {dept?.updated && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                    <Typography variant="body2">
                      {new Date(dept.updated).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Address Dialog */}
      <Dialog open={addressDialogOpen} onClose={handleCloseAddressDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {address ? 'Edit Address' : 'Add Address'}
          <IconButton
            onClick={handleCloseAddressDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Address Line 1"
              value={addressForm.line1}
              onChange={(e) => setAddressForm({ ...addressForm, line1: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Address Line 2"
              value={addressForm.line2}
              onChange={(e) => setAddressForm({ ...addressForm, line2: e.target.value })}
              fullWidth
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  value={addressForm.city}
                  onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="State/Region"
                  value={addressForm.state_region}
                  onChange={(e) => setAddressForm({ ...addressForm, state_region: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Postal Code"
                  value={addressForm.postal_code}
                  onChange={(e) => setAddressForm({ ...addressForm, postal_code: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={countries}
                  getOptionLabel={(option) => `${option.name} (${option.code})`}
                  value={countries.find(c => c.code === addressForm.country_code) || null}
                  onChange={(e, newValue) => {
                    setAddressForm({ ...addressForm, country_code: newValue?.code || 'USA' });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      required
                      placeholder="Search countries..."
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  fullWidth
                />
              </Grid>
            </Grid>
            <FormControl fullWidth>
              <InputLabel>Address Type</InputLabel>
              <Select
                value={addressForm.type}
                onChange={(e) => setAddressForm({ ...addressForm, type: e.target.value })}
                label="Address Type"
              >
                <MenuItem value="business">Business</MenuItem>
                <MenuItem value="residential">Residential</MenuItem>
                <MenuItem value="mailing">Mailing</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddressDialog}>Cancel</Button>
          <Button
            onClick={handleSaveAddress}
            variant="contained"
            disabled={updating || !addressForm.line1 || !addressForm.city}
          >
            {address ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>


    </Container>
  );
};

export default DepartmentPage;
